import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { Box, Button, Flex, HStack, Text, Badge, Portal, Circle, Icon } from '@chakra-ui/react';
import { FaCheckCircle } from 'react-icons/fa';

// A lightweight spotlight product-tour. Each step optionally targets a real DOM
// element by `selector` (a [data-tour="..."] attribute on the live page); the
// tour dims the rest of the screen, outlines the target, and shows a tooltip
// describing it. Steps with no target (or a target that isn't on screen) render
// as a centered info card. `before()` runs before a step measures — use it to
// switch tabs so the target becomes visible.
//
// props:
//   steps    [{ selector?, title, body, placement?: 'auto'|'top'|'bottom', before?() }]
//   isOpen   bool
//   onClose  () => void   (called on finish or skip)

const PAD = 8;              // spotlight padding around the target
const TIP_W = 340;          // tooltip width

export default function PhlebTour({ steps, isOpen, onClose, onComplete }) {
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);   // target rect (viewport coords) or null = centered

  const step = steps[idx];
  const total = steps.length;

  const measure = useCallback(() => {
    if (!step) return;
    const el = step.selector ? document.querySelector(step.selector) : null;
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    // Treat off-screen / zero-size targets as "no target" → centered card.
    if (r.width === 0 && r.height === 0) { setRect(null); return; }
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height, bottom: r.bottom, right: r.right });
  }, [step]);

  // On step change: run before(), scroll the target into view, then measure.
  useLayoutEffect(() => {
    if (!isOpen || !step) return;
    let raf;
    try { step.before?.(); } catch (e) { /* ignore */ }
    const t = setTimeout(() => {
      const el = step.selector ? document.querySelector(step.selector) : null;
      if (el && el.scrollIntoView) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      raf = requestAnimationFrame(() => setTimeout(measure, 300));
    }, 60);
    return () => { clearTimeout(t); if (raf) cancelAnimationFrame(raf); };
  }, [isOpen, idx, step, measure]);

  // Keep the spotlight glued to the target as the page scrolls/resizes.
  useEffect(() => {
    if (!isOpen) return;
    const onMove = () => measure();
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [isOpen, measure]);

  // Reset to the first step whenever the tour is (re)opened.
  useEffect(() => { if (isOpen) setIdx(0); }, [isOpen]);

  if (!isOpen || !step) return null;

  const finish = () => { onClose?.(); };
  const complete = () => { try { onComplete?.(); } catch (e) { /* ignore */ } onClose?.(); };
  const next = () => (idx < total - 1 ? setIdx(idx + 1) : complete());
  const back = () => setIdx(Math.max(0, idx - 1));

  // Tooltip position: below the target if there's room, else above; clamped to
  // the viewport. Centered when there's no target.
  let tipStyle;
  if (rect) {
    const vh = window.innerHeight, vw = window.innerWidth;
    const spaceBelow = vh - rect.bottom;
    const placeBelow = step.placement === 'bottom' || (step.placement !== 'top' && spaceBelow > 220);
    const left = Math.min(Math.max(12, rect.left), vw - TIP_W - 12);
    tipStyle = placeBelow
      ? { top: Math.min(rect.bottom + 14, vh - 20), left, transform: 'none' }
      : { top: Math.max(rect.top - 14, 20), left, transform: 'translateY(-100%)' };
  } else {
    tipStyle = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  }

  return (
    <Portal>
      {/* Click-catcher: blocks interaction with the page underneath. */}
      <Box position="fixed" inset={0} zIndex={2000} onClick={(e) => e.stopPropagation()} />

      {/* Spotlight: a box over the target casting a huge dark shadow to dim the
          rest. Purely visual (no pointer events). Centered steps dim everything. */}
      {rect ? (
        <Box
          position="fixed" zIndex={2001} pointerEvents="none"
          top={`${rect.top - PAD}px`} left={`${rect.left - PAD}px`}
          width={`${rect.width + PAD * 2}px`} height={`${rect.height + PAD * 2}px`}
          borderRadius="10px" border="2px solid" borderColor="white"
          boxShadow="0 0 0 9999px rgba(15,23,42,0.62)"
          transition="all 0.2s ease"
        />
      ) : (
        <Box position="fixed" inset={0} zIndex={2001} pointerEvents="none" bg="rgba(15,23,42,0.62)" />
      )}

      {/* Tooltip / info card */}
      <Box
        position="fixed" zIndex={2002} w={`${TIP_W}px`} maxW="calc(100vw - 24px)"
        bg="white" borderRadius="xl" boxShadow="2xl" p={4}
        style={tipStyle}
      >
        <HStack justify="space-between" mb={2}>
          <Badge colorScheme="blue" borderRadius="full">Step {idx + 1} of {total}</Badge>
          <Button size="xs" variant="ghost" color="gray.500" onClick={finish}>Skip tour</Button>
        </HStack>
        <Text fontWeight="800" fontSize="md" color="gray.800" mb={1}>{step.title}</Text>
        <Text fontSize="sm" color="gray.600">{step.body}</Text>

        {/* progress dots */}
        <HStack spacing={1.5} mt={3} mb={3} justify="center">
          {steps.map((s, i) => (
            <Circle key={i} size="7px" bg={i === idx ? 'blue.500' : i < idx ? 'blue.300' : 'gray.300'} />
          ))}
        </HStack>

        <Flex justify="space-between">
          <Button size="sm" variant="ghost" onClick={back} isDisabled={idx === 0}>Back</Button>
          <Button size="sm" colorScheme="blue" onClick={next}
            rightIcon={idx === total - 1 ? <Icon as={FaCheckCircle} /> : undefined}>
            {idx === total - 1 ? 'Done' : 'Next'}
          </Button>
        </Flex>
      </Box>
    </Portal>
  );
}
